import type { DeptPlacementRow } from "../lib/placements";

/**
 * Students placed per year for one department, oldest to newest — the same
 * series the table beneath it lists, read left to right instead of top down.
 * Each bar is stacked: in-campus below, off-campus above, so the bar's height
 * is the year's total and the split is visible within it.
 *
 * Drawn in the house chart idiom (see PlacementTrend): dashed gridlines on
 * --rule, navy bars, and the .illo-* type classes.
 */
const W = 720;
const H = 300;
const PAD = { top: 40, right: 20, bottom: 62, left: 52 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

/** A clean axis top: the next multiple of `step` above the tallest bar. */
function axisTop(max: number): number {
  const step = max > 200 ? 50 : max > 80 ? 20 : max > 20 ? 10 : 5;
  return Math.max(step, Math.ceil(max / step) * step);
}

export function DeptPlacementChart({
  rows,
  deptName,
}: {
  rows: DeptPlacementRow[];
  deptName: string;
}) {
  // Oldest first so the axis reads left to right, and only years that state a
  // figure — a year with no count would otherwise draw as a zero-height bar
  // and read as "nobody was placed".
  const data = rows.filter((r) => r.total !== null).slice().reverse();
  if (data.length < 2) return null;

  const max = axisTop(Math.max(...data.map((r) => r.total!)));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const slot = PLOT_W / data.length;
  const barW = Math.min(slot / 1.7, 54);
  const anyOff = data.some((r) => (r.offCampus ?? 0) > 0);
  const anyBatch = data.some((r) => r.basis === "batch");
  const source = anyBatch
    ? "department record and BIET Training & Placement Cell"
    : "department record";
  const scale = (n: number) => (n / max) * PLOT_H;

  return (
    <figure className="dept-chart reveal">
      <figcaption className="dept-chart-cap">
        <span className="dept-chart-cap-title">Placed per year</span>
        <span>{deptName} · source: {source}</span>
      </figcaption>
      {/* Scrolls rather than shrinking, like .dept-table-wrap: squeezed into a
          phone's width the axis labels fall to about 5px and stop being
          readable. */}
      <div className="dept-chart-scroll">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`Students placed per year in ${deptName}, split into in-campus and off-campus. ${data
            .map(
              (r) =>
                `${r.year}: ${r.total} total, ${r.inCampus ?? 0} in-campus, ${r.offCampus ?? 0} off-campus`,
            )
            .join(". ")}.`}
        >
          {ticks.map((v) => {
            const y = PAD.top + PLOT_H - scale(v);
            return (
              <g key={v}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={y}
                  y2={y}
                  stroke="var(--rule)"
                  strokeWidth="0.6"
                  strokeDasharray={v === 0 ? "" : "2 4"}
                />
                <text x={PAD.left - 10} y={y + 4} textAnchor="end" className="illo-label" opacity="0.7">
                  {v}
                </text>
              </g>
            );
          })}

          <text x={PAD.left - 38} y={PAD.top - 18} className="illo-label-bold" textAnchor="start">
            PLACED
          </text>
          <text
            x={W - PAD.right}
            y={PAD.top - 18}
            className="illo-label-bold"
            textAnchor="end"
            fill="var(--gold-700)"
          >
            {data[0].year} — {data[data.length - 1].year}
          </text>

          {data.map((r, i) => {
            const cx = PAD.left + slot * (i + 0.5);
            const inC = r.inCampus ?? 0;
            const off = r.offCampus ?? 0;
            const inH = scale(inC);
            const offH = scale(off);
            const base = PAD.top + PLOT_H;
            return (
              <g key={r.year}>
                <rect x={cx - barW / 2} y={base - inH} width={barW} height={inH} fill="var(--navy-700)" />
                {off > 0 && (
                  <rect
                    x={cx - barW / 2}
                    y={base - inH - offH}
                    width={barW}
                    height={offH}
                    fill="var(--gold-600)"
                  />
                )}
                <text
                  x={cx}
                  y={base - inH - offH - 8}
                  textAnchor="middle"
                  className="illo-numeral"
                  style={{ fontSize: "13px" }}
                >
                  {r.total}
                </text>
                <text x={cx} y={H - PAD.bottom + 22} textAnchor="middle" className="illo-label-bold">
                  {r.year}
                </text>
                {r.highestLpa !== null && (
                  <text
                    x={cx}
                    y={H - PAD.bottom + 40}
                    textAnchor="middle"
                    className="illo-label"
                    opacity="0.65"
                  >
                    {r.highestLpa} LPA
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="dept-chart-key">
        <span>
          <i className="k-in" />
          In-campus
        </span>
        <span>
          <i className="k-off" />
          Off-campus
        </span>
        {!anyOff && <span>No off-campus figure in this department's record</span>}
      </div>
    </figure>
  );
}
